import { GridColumns } from '@mui/x-data-grid-pro'
import { ActivityLevel, WasteContainerDto, WasteStatus } from '@services/dtos'
import { useCallback, useEffect, useMemo, useState } from 'react'
import BaseConfigTable from './BaseConfigTable'
import { useAppDispatch } from '@hooks/reduxHooks'
import { AppDispatch, useSelector } from '@state/store'
import WasteContainerForm from '../forms/WasteContainerForm'
import { WasteStatusLabels } from '@utils/constants'
import { addWasteContainerMeasurement, cancelDisposeWasteContainer, cancelTransferWasteContainer, getWasteContainers, removeWasteContainer, reopenWasteContainer, setWasteContainerFilters, updateWasteContainer } from '@state/reducers/wasteContainerReducer'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import LockIcon from '@mui/icons-material/Lock'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import UndoIcon from '@mui/icons-material/Undo'
import ReplayIcon from '@mui/icons-material/Replay'
import HistoryIcon from '@mui/icons-material/History'
import AddchartIcon from '@mui/icons-material/Addchart'
import QueryStatsIcon from '@mui/icons-material/QueryStats'
import { Box, Chip, Dialog, DialogContent, Tooltip, Typography } from '@mui/material'
import { UpTransition } from '../animations'
import WasteStatusIcon from '../WasteStatusIcon'
import { WasteContainersToolbar } from './WasteContainersToolbar'
import { CloseWasteContainerDialog } from '../dialogs/CloseWasteContainerDialog'
import { DisposeWasteContainerDialog } from '../dialogs/DisposeWasteContainerDialog'
import { WasteContainerMeasurementDialog } from '../dialogs/WasteContainerMeasurementDialog'
import { WasteContainerMeasurementsDialog } from '../dialogs/WasteContainerMeasurementsDialog'
import { TransferWasteContainerDialog } from '../dialogs/TransferWasteContainerDialog'
import { WasteContainerLogsDialog } from '../dialogs/WasteContainerLogsDialog'
import WasteEntriesTable from './WasteEntriesTable'
import ConfirmDialog from '../dialogs/ConfirmDialog'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import { FormHeader } from '../forms/FormHeader'
import { getContainerSubTitles } from '@utils/waste.utils'
import { WasteContainerActivityLevelChip, WasteContainerTypeChip } from '../WasteContainerChips'

export default function WasteContainersTable({ showTabs = false }: { showTabs?: boolean }) {
  const dispatch = useAppDispatch()
  const [duplicateWasteContainer, setDuplicateWasteContainer] = useState<WasteContainerDto | null>(null)
  const [duplicateFormOpen, setDuplicateFormOpen] = useState(false)
  const [containerToClose, setContainerToClose] = useState<WasteContainerDto | null>(null)
  const [containerToDispose, setContainerToDispose] = useState<WasteContainerDto | null>(null)
  const [containerToTransfer, setContainerToTransfer] = useState<WasteContainerDto | null>(null)
  const [containerToCancelTransfer, setContainerToCancelTransfer] = useState<WasteContainerDto | null>(null)
  const [containerForEntries, setContainerForEntries] = useState<WasteContainerDto | null>(null)
  const [containerForLogs, setContainerForLogs] = useState<WasteContainerDto | null>(null)
  const [containerToMeasure, setContainerToMeasure] = useState<WasteContainerDto | null>(null)
  const [containerForMeasurements, setContainerForMeasurements] = useState<WasteContainerDto | null>(null)
  const [containerToReopen, setContainerToReopen] = useState<WasteContainerDto | null>(null)
  const [isReopening, setIsReopening] = useState(false)
  const [containerToCancelDispose, setContainerToCancelDispose] = useState<WasteContainerDto | null>(null)
  const [isCancellingDispose, setIsCancellingDispose] = useState(false)
  const [isCancellingTransfer, setIsCancellingTransfer] = useState(false)
  const { filters, containerRows } = useSelector(({ wasteContainer }) => ({
    filters: wasteContainer.filters,
    containerRows: wasteContainer.items.datas,
  }))

  const CHAIN_COLORS = ['success', 'secondary', 'warning', 'error'] as const
  type ChainColor = typeof CHAIN_COLORS[number]

  const chainColorMap = useMemo(() => {
    const toMap = new Map<string, string>()
    for (const c of containerRows) {
      if (c.transferredTo) {
        toMap.set(c.id, c.transferredTo.id)
      }
    }

    const findRoot = (id: string, visited = new Set<string>()): string => {
      if (visited.has(id)) {
        return id
      }
      visited.add(id)
      const nextId = toMap.get(id)
      return nextId ? findRoot(nextId, visited) : id
    }

    const rootColors = new Map<string, number>()
    let nextColorIndex = 0
    const result = new Map<string, { color: ChainColor; isTop: boolean }>()

    for (const c of containerRows) {
      const hasTo = !!c.transferredTo
      const hasFrom = (c.transferredFrom?.length ?? 0) > 0
      if (!hasTo && !hasFrom) {
        continue
      }

      const rootId = findRoot(c.id)
      if (!rootColors.has(rootId)) {
        rootColors.set(rootId, nextColorIndex++ % CHAIN_COLORS.length)
      }
      result.set(c.id, {
        color: CHAIN_COLORS[rootColors.get(rootId)!],
        isTop: !hasTo,
      })
    }
    return result
  }, [containerRows])

  useEffect(() => {
    return () => {
      dispatch(setWasteContainerFilters({ includeInactive: false, status: undefined, type: undefined, activityLevel: undefined }))
    }
  }, [])

  useEffect(() => {
    dispatch(getWasteContainers())
  }, [filters, dispatch])

  const columns: GridColumns<WasteContainerDto> = useMemo(
    () => [
      { field: 'no', headerName: 'N°', width: 70, type: 'number' },
      {
        field: 'status',
        headerName: 'Statut',
        width: 85,
        renderCell: (params) => {
          const hasTo = !!params.row.transferredTo
          const hasFrom = (params.row.transferredFrom?.length ?? 0) > 0
          const justifyClass = hasTo && hasFrom ? 'justify-between' : 'justify-center'
          const chain = chainColorMap.get(params.row.id)
          const chipSx = { height: 16, '& .MuiChip-label': { px: 0.5, py: 0, lineHeight: 1 } }
          return (
            <Box className="flex gap-2">
              <Tooltip title={WasteStatusLabels[params.row.status]}>
                <Box className="flex items-center">
                  <WasteStatusIcon status={params.row.status} />
                  {params.row.wasteEntryCount > 0 && (
                    <Typography fontWeight={500} variant="caption">
                      {params.row.wasteEntryCount}
                    </Typography>
                  )}
                </Box>
              </Tooltip>
              <Box className={`flex flex-col ${justifyClass} gap-1`}>
                {hasTo && (
                  <Tooltip title={`Transvasée vers ${params.row.transferredTo?.label}`}>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={chain?.color ?? 'default'}
                      label="→"
                      sx={chipSx}
                    />
                  </Tooltip>
                )}
                {hasFrom && (
                  <Tooltip
                    title={`A reçu : ${params.row.transferredFrom.length} tranvasement${params.row.transferredFrom.length > 1 ? 's' : ''}`}
                  >
                    <Chip
                      size="small"
                      variant="outlined"
                      color={chain?.color ?? 'default'}
                      sx={{ ...chipSx, ...(chain?.isTop ? { borderWidth: 2 } : {}) }}
                      label={
                        <span className={`flex items-center gap-0.5${chain?.isTop ? ' font-bold' : ''}`}>
                          ←
                          <span className="text-[10px]">
                            {params.row.transferredFrom.length}
                          </span>
                        </span>
                      }
                    />
                  </Tooltip>
                )}
              </Box>
            </Box>
          )
        }
      },
      { field: 'code', headerName: 'Code', width: 80 },
      { field: 'label', headerName: 'Libellé', width: 260 },
      {
        field: 'type',
        headerName: 'Type',
        width: 85,
        renderCell: (params) => <WasteContainerTypeChip container={params.row} />,
      },
      {
        field: 'activityLevel',
        headerName: 'Activité',
        width: 130,
        renderCell: (params) => <WasteContainerActivityLevelChip container={params.row} />,
      },
      {
        field: 'activityAtRefMbq',
        headerName: 'MBq estimé',
        width: 130,
        type: 'number',
        renderCell: (params) => {
          const row = params.row as WasteContainerDto
          if (row.activityAtRefMbq == null) {
            return null
          }
          if (row.activityAtTimeMbq === null) {
            return <span>{row.activityAtRefMbq.toFixed(2)}</span>
          }
          return (
            <div className="flex items-center gap-1">
              <span className="font-medium">{row.activityAtTimeMbq.toFixed(2)}</span>
              <span>/ {row.activityAtRefMbq.toFixed(2)}</span>
            </div>
          )
        },
      },
      {
        field: 'isotopes',
        headerName: 'Isotopes',
        width: 200,
        valueGetter: (params) => params.row.isotopes.map(({ name, code }) => `${name} (${code})`).join(', '),
      },
      { field: 'isInfectious', headerName: 'DASRI', width: 70, type: 'boolean' },
      { field: 'tareKg', headerName: 'Tare (kg)', width: 80, type: 'number' },
      { field: 'volumeL', headerName: 'Volume (L)', width: 90, type: 'number' },
      {
        field: 'storageLocation',
        headerName: 'Stockage',
        width: 160,
        valueGetter: (params) => params.row.storageLocation?.name,
      },
      { field: 'location', headerName: 'Emplacement', width: 200 },
      { field: 'notes', headerName: 'Notes', width: 300 },
    ],
    [chainColorMap],
  )

  return (
    <>
      <BaseConfigTable<WasteContainerDto>
        columns={columns}
        selectRows={(state) => state.wasteContainer.items}
        fetchThunk={() => async (dispatch: AppDispatch) => {
          dispatch(setWasteContainerFilters({ includeInactive: true }))
          return dispatch(getWasteContainers())
        }}
        extraActions={(row) => [
          ...(row.wasteEntryCount > 0 ? [{
            key: 'entries',
            label: 'Voir les déchets',
            icon: <FormatListBulletedIcon fontSize="small" color="primary" />,
            onClick: (row: WasteContainerDto) => setContainerForEntries(row),
          }] : []),
          ...(row.status === WasteStatus.OPEN ? [{
            key: 'close',
            label: 'Fermer',
            icon: <LockIcon fontSize="small" color="primary" />,
            onClick: (row: WasteContainerDto) => setContainerToClose(row),
          }] : []),
          ...(row.status !== WasteStatus.DISPOSED && !row.transferredTo ? [{
            key: 'transfer',
            label: 'Transvaser',
            disabled: row.status === WasteStatus.OPEN,
            disabledTooltip: 'Le conteneur doit être fermé',
            icon: <SwapHorizIcon fontSize="small" color={row.status === WasteStatus.OPEN ? 'disabled' : 'primary'} />,
            onClick: (row: WasteContainerDto) => setContainerToTransfer(row),
          }] : []),
          ...(row.status === WasteStatus.CLOSED ? [{
            key: 'dispose',
            label: 'Éliminer',
            disabled: !!row.transferredTo,
            disabledTooltip: 'Impossible car transvasée',
            icon: <HighlightOffIcon fontSize="small" color={row.transferredTo ? 'disabled' : 'primary'} />,
            onClick: (row: WasteContainerDto) => setContainerToDispose(row),
          }] : []),
          {
            key: 'measure',
            label: 'Ajouter une mesure',
            icon: <AddchartIcon fontSize="small" color="primary" />,
            onClick: (row: WasteContainerDto) => setContainerToMeasure(row),
          },
          ...(row.logsCount > 0 ? [{
            key: 'logs',
            label: 'Consulter l\'historique',
            icon: <HistoryIcon fontSize="small" color="primary" />,
            onClick: (row: WasteContainerDto) => setContainerForLogs(row),
          }] : []),
        ]}
        menuActions={(row) => [
          {
            key: 'duplicate',
            label: 'Dupliquer',
            icon: <ContentCopyIcon color="primary" fontSize="small" />,
            onClick: (row: WasteContainerDto) => {
              setDuplicateWasteContainer(row)
              setDuplicateFormOpen(true)
            },
          },
          ...(row.status === WasteStatus.CLOSED && !row.transferredTo ? [{
            key: 'reopen',
            label: 'Rouvrir',
            icon: <LockOpenIcon fontSize="small" color="primary" />,
            onClick: (row: WasteContainerDto) => setContainerToReopen(row),
          }] : []),
          ...(row.transferredTo ? [{
            key: 'cancelTransfer',
            label: 'Annuler le transvasement',
            icon: <UndoIcon fontSize="small" color="primary" />,
            onClick: (row: WasteContainerDto) => setContainerToCancelTransfer(row),
          }] : []),
          ...(row.status === WasteStatus.DISPOSED ? [{
            key: 'cancelDispose',
            label: 'Annuler l\'élimination',
            icon: <ReplayIcon fontSize="small" color="primary" />,
            onClick: (row: WasteContainerDto) => setContainerToCancelDispose(row),
          }] : []),
          ...(row.measurementsCount > 0 ? [{
            key: 'measurements',
            label: 'Consulter les mesures',
            icon: <QueryStatsIcon fontSize="small" color="primary" />,
            onClick: (row: WasteContainerDto) => setContainerForMeasurements(row),
          }] : []),
        ]}
        extraWidth={160}
        deleteThunk={removeWasteContainer}
        toggleThunk={(id, isActive) => updateWasteContainer({ id, dto: { isActive } })}
        updatePageThunk={(page) => async (dispatch: AppDispatch) => {
          dispatch(setWasteContainerFilters({ page }))
          dispatch(getWasteContainers())
        }}
        FormComponent={useCallback(({ editingItem, onClose }) => (
          <WasteContainerForm editingWasteContainer={editingItem} onClose={onClose} />
        ), [])}
        resourceLabel="conteneur à déchets"
        resourcePrefix="le "
        formMaxWidth="md"
        toolbarExtras={<WasteContainersToolbar showTabs={showTabs} />}
        toolbarExtrasPosition="left"
        exportCsvOptions={{ fileName: 'waste-containers', delimiter: ';', utf8WithBom: true }}
      />

      <Dialog
        fullWidth
        maxWidth="md"
        open={duplicateFormOpen}
        TransitionComponent={UpTransition}
        onClose={() => setDuplicateFormOpen(false)}
      >
        <DialogContent>
          <WasteContainerForm
            duplicateCase
            editingWasteContainer={duplicateWasteContainer}
            onClose={() => setDuplicateFormOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {containerToClose && (
        <CloseWasteContainerDialog
          open
          container={containerToClose}
          onClose={() => setContainerToClose(null)}
        />
      )}

      {containerToMeasure && (
        <WasteContainerMeasurementDialog
          open
          container={containerToMeasure}
          onClose={() => setContainerToMeasure(null)}
          title={`Ajouter mesure conteneur #${containerToMeasure.no}`}
          successMessage="Mesure ajoutée avec succès"
          errorMessage="Une erreur est survenue lors de l'ajout de la mesure"
          forceActivity
          onSubmit={(dto) => dispatch(addWasteContainerMeasurement({ id: containerToMeasure.id, dto })).unwrap()}
        />
      )}

      {containerForMeasurements && (
        <WasteContainerMeasurementsDialog
          container={containerForMeasurements}
          onClose={() => setContainerForMeasurements(null)}
        />
      )}

      {containerToTransfer && (
        <TransferWasteContainerDialog
          open
          container={containerToTransfer}
          onClose={() => setContainerToTransfer(null)}
        />
      )}

      <Dialog
        open={!!containerForEntries}
        onClose={() => setContainerForEntries(null)}
        fullWidth
        maxWidth="xl"
        PaperProps={{ sx: { height: '80vh' } }}
      >
        <div className="px-12 pt-6">
          <FormHeader
            title={`Déchets #${containerForEntries?.no}`}
            subTitles={containerForEntries ? getContainerSubTitles(containerForEntries) : []}
            onClose={() => setContainerForEntries(null)}
            isSubmitting={false}
            showSubmit={false} />
        </div>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', pt: 1, overflow: 'hidden' }}>
          {containerForEntries && (
            <WasteEntriesTable containerId={containerForEntries.id} />
          )}
        </DialogContent>
      </Dialog>

      {containerForLogs && (
        <WasteContainerLogsDialog
          container={containerForLogs}
          onClose={() => setContainerForLogs(null)}
        />
      )}

      <ConfirmDialog
        open={!!containerToCancelDispose}
        title={`Annuler l'élimination du conteneur #${containerToCancelDispose?.no}`}
        message={`Voulez-vous vraiment annuler l'élimination du conteneur "${containerToCancelDispose?.label}" ?`}
        confirmButtonText="Confirmer"
        severity="warning"
        isProcessing={isCancellingDispose}
        onCancel={() => setContainerToCancelDispose(null)}
        onConfirm={async () => {
          if (!containerToCancelDispose) return
          setIsCancellingDispose(true)
          dispatch(cancelDisposeWasteContainer(containerToCancelDispose.id))
            .unwrap()
            .then(() => {
              dispatch(enqueueSnackbar({ message: 'Élimination annulée avec succès', options: { variant: 'success' } }))
              setContainerToCancelDispose(null)
            })
            .catch(() =>
              dispatch(enqueueSnackbar({ message: 'Une erreur est survenue', options: { variant: 'error' } })),
            )
            .finally(() => setIsCancellingDispose(false))
        }}
      />

      <ConfirmDialog
        open={!!containerToReopen}
        title={`Rouvrir le conteneur #${containerToReopen?.no}`}
        message={`Voulez-vous vraiment rouvrir le conteneur "${containerToReopen?.label}" ?${containerToReopen?.activityLevel !== ActivityLevel.NONE ? ' La mesure d\'activité de fermeture sera supprimée.' : ''}`}
        severity="warning"
        isProcessing={isReopening}
        onCancel={() => setContainerToReopen(null)}
        onConfirm={async () => {
          if (!containerToReopen) return
          setIsReopening(true)
          dispatch(reopenWasteContainer(containerToReopen.id))
            .unwrap()
            .then(() => {
              dispatch(enqueueSnackbar({ message: 'Conteneur rouvert avec succès', options: { variant: 'success' } }))
              setContainerToReopen(null)
            })
            .catch(() =>
              dispatch(enqueueSnackbar({ message: 'Une erreur est survenue', options: { variant: 'error' } })),
            )
            .finally(() => setIsReopening(false))
        }}
      />

      {containerToDispose && (
        <DisposeWasteContainerDialog
          open={!!containerToDispose}
          container={containerToDispose}
          onClose={() => setContainerToDispose(null)}
        />
      )}

      <ConfirmDialog
        open={!!containerToCancelTransfer}
        title="Annuler le transvasement"
        message={`Voulez-vous annuler le transvasement du conteneur "${containerToCancelTransfer?.label}" vers "${containerToCancelTransfer?.transferredTo?.label}" ?`}
        confirmButtonText="Confirmer"
        severity="warning"
        isProcessing={isCancellingTransfer}
        onCancel={() => setContainerToCancelTransfer(null)}
        onConfirm={async () => {
          if (!containerToCancelTransfer) {
            return
          }
          setIsCancellingTransfer(true)
          dispatch(cancelTransferWasteContainer(containerToCancelTransfer.id))
            .unwrap()
            .then(() => {
              dispatch(enqueueSnackbar({ message: 'Transvasement annulé avec succès', options: { variant: 'success' } }))
              setContainerToCancelTransfer(null)
            })
            .catch(() =>
              dispatch(enqueueSnackbar({ message: 'Une erreur est survenue lors de l\'annulation', options: { variant: 'error' } })),
            )
            .finally(() => setIsCancellingTransfer(false))
        }}
      />
    </>
  )
}
